import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import { useBreakpoint } from '../hooks/useBreakpoint';
import api from '../api/axios';

const C = { primary: '#8B1A1A', gold: '#D4A017' };

export default function Profile() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { addToast } = useToast();
  const { isMobile } = useBreakpoint();

  const [form, setForm] = useState({
    first_name: user?.first_name || '',
    last_name: user?.last_name || '',
    email: user?.email || '',
  });
  const [pwForm, setPwForm] = useState({ old_password: '', new_password: '', confirm_password: '' });
  const [profileLoading, setProfileLoading] = useState(false);
  const [pwLoading, setPwLoading] = useState(false);
  const [pwError, setPwError] = useState('');

  if (!user) { navigate('/login'); return null; }

  const inputStyle = {
    width: '100%', padding: '11px 14px', border: '1.5px solid #e0d0d0',
    borderRadius: 4, fontSize: 14, outline: 'none', boxSizing: 'border-box',
  };
  const labelStyle = { display: 'block', fontSize: 13, fontWeight: 600, color: '#555', marginBottom: 6 };
  const cardStyle = { background: '#fff', borderRadius: 8, boxShadow: '0 2px 10px rgba(0,0,0,0.07)', padding: isMobile ? '20px 16px' : '28px 28px', marginBottom: 20 };

  const handleProfileSave = async (e) => {
    e.preventDefault();
    setProfileLoading(true);
    try {
      await api.patch('/auth/profile/', { first_name: form.first_name, last_name: form.last_name });
      addToast('Profile updated successfully', 'success');
    } catch {
      addToast('Failed to update profile', 'error');
    } finally {
      setProfileLoading(false);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setPwError('');
    if (pwForm.new_password !== pwForm.confirm_password) { setPwError('New passwords do not match.'); return; }
    if (pwForm.new_password.length < 8) { setPwError('Password must be at least 8 characters.'); return; }
    setPwLoading(true);
    try {
      await api.post('/auth/change-password/', { old_password: pwForm.old_password, new_password: pwForm.new_password });
      addToast('Password changed successfully', 'success');
      setPwForm({ old_password: '', new_password: '', confirm_password: '' });
    } catch (err) {
      setPwError(err.response?.data?.detail || err.response?.data?.old_password?.[0] || 'Failed to change password.');
    } finally {
      setPwLoading(false);
    }
  };

  const handleLogout = () => { logout(); navigate('/'); };

  return (
    <div style={{ maxWidth: 680, margin: '0 auto', padding: isMobile ? '24px 16px' : '40px 24px' }}>
      <h1 style={{ fontFamily: "'Playfair Display', serif", fontSize: isMobile ? 26 : 32, color: '#2C2C2C', marginBottom: 4 }}>My Account</h1>
      <p style={{ color: '#888', marginBottom: 24, fontSize: 14 }}>@{user.username}</p>

      <div style={{ display: 'flex', gap: 12, marginBottom: 28, flexWrap: 'wrap' }}>
        {[{ label: '📦 My Orders', to: '/my-orders' }, { label: '♡ Wishlist', to: '/wishlist' }].map(l => (
          <Link key={l.to} to={l.to} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 18px', background: '#FFF8F0', borderRadius: 6, textDecoration: 'none', color: C.primary, fontSize: 14, fontWeight: 600, border: '1px solid #f0e0e0' }}>
            {l.label}
          </Link>
        ))}
      </div>

      <div style={cardStyle}>
        <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: 20, color: '#2C2C2C', marginBottom: 20 }}>Personal Information</h2>
        <form onSubmit={handleProfileSave}>
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 16, marginBottom: 16 }}>
            <div>
              <label style={labelStyle}>First Name</label>
              <input style={inputStyle} value={form.first_name} onChange={e => setForm(f => ({ ...f, first_name: e.target.value }))} placeholder="First name" />
            </div>
            <div>
              <label style={labelStyle}>Last Name</label>
              <input style={inputStyle} value={form.last_name} onChange={e => setForm(f => ({ ...f, last_name: e.target.value }))} placeholder="Last name" />
            </div>
          </div>
          <div style={{ marginBottom: 16 }}>
            <label style={labelStyle}>Username</label>
            <input style={{ ...inputStyle, background: '#f9f9f9', color: '#aaa' }} value={user.username} readOnly />
          </div>
          <div style={{ marginBottom: 20 }}>
            <label style={labelStyle}>Email</label>
            <input style={{ ...inputStyle, background: '#f9f9f9', color: '#aaa' }} value={form.email} readOnly />
          </div>
          <button type="submit" disabled={profileLoading}
            style={{ background: profileLoading ? '#ccc' : C.primary, color: '#fff', border: 'none', padding: '11px 28px', borderRadius: 4, fontSize: 14, fontWeight: 600, cursor: profileLoading ? 'not-allowed' : 'pointer', width: isMobile ? '100%' : 'auto' }}
          >
            {profileLoading ? 'Saving…' : 'Save Changes'}
          </button>
        </form>
      </div>

      <div style={cardStyle}>
        <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: 20, color: '#2C2C2C', marginBottom: 20 }}>Change Password</h2>
        <form onSubmit={handlePasswordChange}>
          {[
            { label: 'Current Password', key: 'old_password', ac: 'current-password' },
            { label: 'New Password', key: 'new_password', ac: 'new-password' },
            { label: 'Confirm New Password', key: 'confirm_password', ac: 'new-password' },
          ].map(f => (
            <div key={f.key} style={{ marginBottom: 14 }}>
              <label style={labelStyle}>{f.label}</label>
              <input type="password" style={inputStyle} value={pwForm[f.key]} onChange={e => setPwForm(p => ({ ...p, [f.key]: e.target.value }))} required autoComplete={f.ac} minLength={f.key !== 'old_password' ? 8 : undefined} />
            </div>
          ))}
          {pwError && <p style={{ color: '#e53935', fontSize: 13, marginBottom: 12 }}>{pwError}</p>}
          <button type="submit" disabled={pwLoading}
            style={{ background: pwLoading ? '#ccc' : C.primary, color: '#fff', border: 'none', padding: '11px 28px', borderRadius: 4, fontSize: 14, fontWeight: 600, cursor: pwLoading ? 'not-allowed' : 'pointer', width: isMobile ? '100%' : 'auto' }}
          >
            {pwLoading ? 'Changing…' : 'Change Password'}
          </button>
        </form>
      </div>

      <div style={{ textAlign: isMobile ? 'center' : 'right' }}>
        <button onClick={handleLogout} style={{ background: '#fff', color: '#e53935', border: '1.5px solid #e53935', padding: '10px 24px', borderRadius: 4, fontSize: 14, fontWeight: 600, cursor: 'pointer', width: isMobile ? '100%' : 'auto' }}>
          Sign Out
        </button>
      </div>
    </div>
  );
}
